import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import DatePicker from "components/common/DatePicker";
import Gender from "constants/type/user/Gender";
import AppointmentService from "modules/appointments/service/AppointmentService";
import CreateAppointmentService from "modules/appointments/service/CreateAppointmentService";
import PatientService from "modules/patient/service/PatientService";
import { formatAgeLabel, formatDob } from "modules/patient/helper/patientFormatters";
import { maskPhone } from "util/maskPhone";
import { formatTimeSlot } from "modules/appointments/helper/formatters";
import { MODES_WITH_AUTO_SP } from "modules/appointments/helper/appointmentConstants";
import PaymentLinkDialog from "modules/appointments/components/payment/PaymentLinkDialog";
import MissingProfileDialog from "modules/appointments/components/MissingProfileDialog";
import "../styles/createAppointment.scss";

/**
 * The cascade, in order. `bookingfor` (which family member) sits before service
 * because it changes memberPatientId for every downstream call. `reason` is in
 * here on purpose: it is free-text clinical detail, and leaving it out meant one
 * patient's "Other" reason survived a patient change and would have been
 * submitted against the next. Anything downstream of a change gets cleared.
 */
const STEPS = [
  "patient", "account", "plan", "bookingfor", "service", "mode", "date", "slot", "reason",
];

/** The exact mode strings the server keys on (bridge MODES). */
const MODE_IN_PERSON = "In person consultation";
const MODE_TELE = "Tele consultation";
/* "Video consultation" is deliberately NOT a constant here: it is excluded by
   absence from SUPPORTED_MODES below, and an unused binding is a lint error in
   a CI build. The name is spelled out in that comment instead. */

/* Compared on a normalised form rather than raw ===: the catalogue is the only
   source of these strings and a stray double space or a capital P would
   otherwise silently drop a mode. Used by BOTH predicates below, which is the
   point — a mode the gate accepts loosely but isInPersonMode rejects strictly
   would be offered and then booked with the wrong payload (no clinic, no
   vendor). */
const normMode = (m) => String(m == null ? "" : m).trim().replace(/\s+/g, " ").toLowerCase();

const isInPersonMode = (m) => normMode(m) === normMode(MODE_IN_PERSON);

/**
 * The modes this screen will BOOK: IN PERSON and TELE consultation.
 * VIDEO consultation is not bookable (product, via PM, 2026-09-10).
 *
 * This REPLACES the 2026-09-09 rule, which was the inverse — tele/video only,
 * in-person blocked. The in-person branches further down (the clinic picker,
 * the vendor fields, the skipped reason) are LIVE again, which is exactly why
 * that change left them in place instead of deleting them.
 *
 * NAMED, NOT PATTERN-MATCHED. The predicate used to be /video|tele/i, which
 * cannot express this rule at all: "Tele consultation" and "Video
 * consultation" are two separate modes in the catalogue (bridge's frozen MODES
 * enum, navigo-module/helper/appointmentConstants.js:56-64) and that regex
 * matches both. Listing the allowed modes makes the rule the thing you read,
 * and makes the next reversal a one-line edit.
 *
 * This one predicate is still the whole gate: it filters the SERVICE list (a
 * service offering only Video never appears) and the MODE list (Video is never
 * an option on a service that offers it alongside another mode), so an
 * unsupported mode cannot be shown, selected, or reached by resetting back to
 * the mode step. A service with nothing left says so through the existing
 * "no bookable consultation mode" note.
 */
const SUPPORTED_MODES = [MODE_IN_PERSON, MODE_TELE];
const isSupportedMode = (m) => SUPPORTED_MODES.some((s) => normMode(s) === normMode(m));

/**
 * The service field's label — bridge's "Select service code/name" builder
 * (navigo-module/CreateAppointment.js:3027-3056):
 *   "(20) ML15 / Nutrition consultation (Tele consultation) (at Rs. 700)"
 * "(serviceId) serviceCode / name (mode / mode)", then "(at Rs. X)" for one
 * mode or "(starting from Rs. X)" for several. X is the FIRST mode's
 * discountedPrice — the cheapest, the server sorts them — printed raw ("Rs."
 * with a dot, no grouping), exactly as bridge prints it.
 *
 * ONE deliberate difference: only the modes this screen can BOOK are listed and
 * priced (no Video — product, 2026-09-10), so the label never advertises a mode,
 * or a "starting from" price, that the mode field will not then offer.
 *
 * The wire's `name` is the display name and `serviceCode` the code — the
 * catalogue transform swaps them (api-client-config-service transform/plan.go).
 * There is no `serviceName` on this response.
 */
/**
 * Bridge's price text on this form: "Rs " and the number as the server sends it
 * — no digit grouping, no decimals added ("Rs 1500", "Rs 0"). "" when there is no
 * number, so a missing price is never printed as "Rs undefined".
 */
const rupees = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && !isNaN(n) ? `Rs ${n}` : "";
};

const serviceLabel = (s) => {
  if (!s) return "";
  const txt = (v) => (v === null || v === undefined ? "" : v);
  const det = (Array.isArray(s.serviceModeDetails) ? s.serviceModeDetails : []).filter(
    (m) => m && isSupportedMode(m.modeName)
  );
  const modes = det.map((m) => m.modeName).join(" / ");
  const price = det.length ? det[0].discountedPrice : undefined;
  const priceText =
    price === undefined || price === null
      ? ""
      : det.length === 1
      ? ` (at Rs. ${price})`
      : ` (starting from Rs. ${price})`;
  return `(${txt(s.serviceId)}) ${txt(s.serviceCode)} / ${txt(s.name)} (${modes})${priceText}`;
};

/**
 * A provider in BRIDGE'S wording (decision 2026-09-14: "follow bridge for
 * wordings") — legacy's getSPLabelTypeConsult (navigo-module/
 * CreateAppointment.js), the raw number with no grouping:
 *   · tele/video: "(123456) Dr. Vijay — MD, Endocrinology (Rs 0)";
 *   · IN PERSON, with the place the visit is at:
 *       - an OPD vendor doctor (legacy's "Apollo" row: `vendorDoctorId` set)
 *         shows the VENDOR DOCTOR id and the clinic, `address.housing`:
 *         "(88231) Dr. Sonal Chavan - Dr. Sonnal Chavaan Aesthetic Clinic (Rs 0)";
 *       - any other provider its own id, city and landmark:
 *         "(123456) Dr. Vijay - Pune - Baner (Rs 0)".
 * Every OPD vendor doctor comes back under the ONE internal provider id its
 * vendor is mapped to (api-client-config-service handleOpdProviders), so in
 * person the `id` alone named a dozen different doctors "(189)" (2026-09-21).
 *
 * The row has no qualification or specialty field (PatientProviderDetails):
 * "— MD, Endocrinology" is whatever the catalogue's `name` holds. The SAME
 * string is the option, the closed value and what search matches, in the
 * picker and the read-only line.
 *
 * Two guards legacy lacks: with no price the parenthetical is dropped (legacy
 * prints "(Rs undefined)"), and an empty city or landmark is skipped rather
 * than printed as " -  - ".
 */
const providerLabel = (p, inPerson) => {
  if (!p) return "";
  const money = rupees(p.price);
  const tail = money ? ` (${money})` : "";
  if (!inPerson) return `(${p.id}) ${p.name || ""}${tail}`;
  const a = p.address || {};
  const vendorDoctor = !!p.vendorDoctorId;
  const where = (vendorDoctor ? [a.housing] : [a.city, a.landmark])
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean)
    .join(" - ");
  return `(${vendorDoctor ? p.vendorDoctorId : p.id}) ${p.name || ""}${where ? ` - ${where}` : ""}${tail}`;
};

/**
 * A step that is still loading says so (2026-09-21). Where a field exists while
 * its data loads, it already says "Loading…" in its own placeholder (plan,
 * service, slot); this line stands in where there is no field yet — the
 * patient search and the patient's record.
 */
const LoadingNote = ({ show, text }) =>
  show ? (
    <p className="cf-note cf-loading" role="status">
      <span className="cf-spinner" aria-hidden="true" />
      {text}
    </p>
  ) : null;
LoadingNote.propTypes = { show: PropTypes.bool, text: PropTypes.string.isRequired };

/* What is loading right now, for those hints. Worked out HERE, outside the
   component, and read below as plain flags: this component sits at the edge of
   eslint-plugin-react-hooks' path counting (see providerKey), and every `&&`,
   `||` or `?:` in its body doubles that count. */
const loadingHints = ({ busy, profiles, patient, searchBy, planBlocked }) => ({
  // the lookup itself — nothing to show under yet
  searchPhone: busy === "patient" && profiles === null && searchBy === "phone",
  searchPid: busy === "patient" && profiles === null && searchBy === "pid",
  // a profile is picked; its full record is on the way
  record: busy === "patient" && profiles !== null && !patient,
  account: busy === "account",
  // "member" is the chosen family member's record, which gates the services
  service: busy === "service" || busy === "member",
  provider: busy === "provider" && !planBlocked,
});
const anyOf = (...xs) => xs.some(Boolean);
const loadingOr = (isLoading, text) => (isLoading ? "Loading…" : text);
/* The account field is drawn while its list loads — it used to be absent, and
   with it the whole Plan & service section. */
const accountVisible = (patient, accounts, loading) => !!patient && (accounts.length > 0 || loading);
const providerReadonly = (p, inPerson, loading) => (loading ? "Loading…" : providerLabel(p, inPerson));

/**
 * WHICH ROW, not which provider. The `id` is not unique in person: every OPD
 * vendor doctor arrives under the one internal provider its vendor is mapped
 * to, told apart only by the vendor, the vendor doctor and the clinic
 * (`address.providerAddressId`). Keyed on `id` alone, the picker marked every
 * "(189)" row as the selection and a click on another one re-picked the first
 * (2026-09-21). Legacy never hit this: it keeps the chosen row OBJECT.
 *
 * Null-safe HERE, not with a ternary at the call site: this component is at the
 * edge of eslint-plugin-react-hooks' path counting, and one more branch in the
 * render body tips it into a false "hook called conditionally".
 */
const providerKey = (p) =>
  p
    ? [p.id, p.vendorId || "", p.vendorDoctorId || 0, (p.address && p.address.providerAddressId) || ""].join("|")
    : null;

/* With "choose automatically" ticked there is no chosen provider to bound the
   date by; bridge leaves the picker unbounded (getMinDate1/getMaxDate1). phr's
   DatePicker needs a number, so a year out stands in for "no maximum" — the
   server still refuses a date the auto-selected provider cannot take. */
const AUTO_SP_MAX_DAYS = 365;

/** reasonId 6 = "Other" (models/constants.go REASON_OTHER); details required. */
const REASON_OTHER = 6;

/** DOB is epoch-millis on the wire; bridge treats 0/absent/NaN as "missing". */
const isDobMissing = (p) => {
  const d = p && p.dateOfBirth;
  return d === undefined || d === null || d === "" || isNaN(Number(d)) || Number(d) === 0;
};
const isEmailMissing = (p) =>
  !p || typeof p.email !== "string" || p.email.trim() === "";

/**
 * Book a consultation. Ops-style: the operator picks the provider from every
 * provider offering the chosen plan/service/mode. Their own rows, if they have
 * any, are marked "(you)" and sorted first.
 *
 * This screen USED to pin the provider to the logged-in user and refuse to book
 * otherwise (asked to be widened 2026-09-03). `identity` survives that change
 * but is now only a LABEL — nothing gates on it, so an ops employee with no
 * provider record of their own books like anyone else.
 *
 * The core call chain was verified live against dev1 (2026-07-15) — see
 * scratchpad/create-appointment-VERIFIED.md. Load-bearing facts:
 *
 * - Only the provider row carries the real `price`. serviceModeDetails[]
 *   .discountedPrice is an indicative label and is NOT what gets charged. Price
 *   therefore follows the SELECTED provider and is re-read on every change.
 * - Rows with id 0 are excluded-provider placeholders the transform pre-sizes
 *   into the array. They carry price 0, so they must never reach the picker: an
 *   operator choosing one would book a paid consultation as free.
 * - Most plans a patient "has" are not bookable: 19 of 31 real combos answer
 *   400 "No active plan exist for patient", and the plans response cannot tell
 *   you which (every plan reports status:1 regardless). So that 400 is a NORMAL
 *   answer rendered inline, not an error toast.
 * - In-person is a SIMPLIFIED path: the visit is at the SELECTED provider's own
 *   address — no location search. It carries no reason (bridge skips it for
 *   in-person). Note bridge refuses to query in-person without a city or
 *   radius; phr has no location UI, so an in-person list is tenant-wide with no
 *   distance signal. That is a usability gap, not a correctness one — the
 *   address still belongs to the provider who is actually being booked.
 * - A provider row carries ONE `address` (consultationInfo.address). The
 *   deployed provider endpoint sends no `addresses[]` list: that field lived
 *   only on api-client-config-service's development-rm branch and release
 *   dropped it. So there is nothing to pick. Legacy sends
 *   `address.providerAddressId ?? ""` to slots and the slots' echo to create,
 *   and so does this (2026-09-21 — requiring a pick from `addresses[]` blocked
 *   every in-person booking with "no clinic address on file"). A row that DOES
 *   bring `addresses[]` still gets the picker.
 */
/** A provider address on one line — "Housing, Area, Landmark, City, State,
    Pincode", skipping what is empty (the pincode is 0 when unknown). */
const addressLine = (a) =>
  a
    ? [a.housing, a.area, a.landmark, a.city, a.state, a.pincode]
        .map((v) => (typeof v === "string" ? v.trim() : v))
        .filter(Boolean)
        .join(", ")
    : "";

/** The board's forward arrow — in the phone field (3041:9992) and on the
    primary button (3041:10198), both 15x15. */
const ArrowIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none"
    stroke="currentColor" strokeWidth="1.7"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8h10" />
    <path d="M9 4l4 4-4 4" />
  </svg>
);

/**
 * A field that is DERIVED rather than asked. The board draws these exactly like
 * the editable ones — same label, same 40px box (3041:10016) — because they are
 * part of the same record; they are simply not questions. An em dash rather than
 * an empty box, so "we don't know" is visibly different from "nothing here yet".
 */
const ReadField = ({ label, required, value, className }) => (
  <div className={className ? `cf-field ${className}` : "cf-field"}>
    <label>
      {label}
      {required && <span className="cf-req">*</span>}
    </label>
    <div className="cf-readonly">{value || <span className="cf-empty">&mdash;</span>}</div>
  </div>
);

ReadField.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  value: PropTypes.node,
  className: PropTypes.string,
};

const CreateAppointment = ({ history }) => {
  const service = useRef(new CreateAppointmentService()).current;
  const apptService = useRef(new AppointmentService()).current;
  const patientService = useRef(new PatientService()).current;

  // provider choice: the list for the current plan+service+mode, and the pick.
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState(null);

  // the cascade
  /* TWO ways in (the id route was asked for 2026-09-03). By PHONE: one number
     can carry several people, so the search answers a LIST and the operator
     picks. By PATIENT ID: an id identifies exactly one, so there is nothing to
     pick and the profile step is skipped. Either way the resolved patientId is
     what every call below is keyed on, and `pidInput` ends up holding it — so
     the field is both an input and the echo it always was. */
  const [phoneInput, setPhoneInput] = useState("");
  const [pidInput, setPidInput] = useState("");
  /* "phone" | "pid" — WHICH box you search from. Both stay on screen either
     way, because the idle one is still the echo it always was: a phone search
     fills the id, an id search fills the phone. The switch decides where the
     search button and the Enter key live, so there is never a question of which
     field a search is about to use. */
  const [searchBy, setSearchBy] = useState("phone");
  const [countryCode] = useState(91);
  const [profiles, setProfiles] = useState(null);   // null = not searched yet
  const [profilesPartial, setProfilesPartial] = useState(false);
  const [patient, setPatient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("");
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(null);
  const [mode, setMode] = useState("");
  const [provider, setProvider] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reasonId, setReasonId] = useState(null);
  const [reasonDetails, setReasonDetails] = useState("");

  // vendor echo — from the SLOTS response only, never the provider object
  const [vendor, setVendor] = useState({
    vendorId: "",
    vendorCode: "",
    vendorDoctorId: 0,
    providerAddressId: "",
  });

  // Self-only guard. An appointment can ONLY be booked with the logged-in user
  // as the service provider — never another provider. `identity` is:
  //   "loading"  – still resolving
  //   an ARRAY   – the provider row ids this user owns (usually one)
  //   "none"     – the account has no provider identity at all → cannot book
  //   "error"    – the lookup failed → cannot book (fail closed, never unpinned)
  // There is NO "operator picks anyone" mode: that is exactly what the rule
  // forbids, and it was silently the default for employee logins before.
  const [identity, setIdentity] = useState("loading");
  const myProviderIds = Array.isArray(identity) ? identity : null;
  const isMine = useCallback(
    (id) => !!myProviderIds && myProviderIds.indexOf(Number(id)) !== -1,
    [myProviderIds]
  );

  // Booking for. memberSel null = nobody chosen yet; a row whose memberPatientId
  // equals the patient's = the patient themself (relation 0 / Self).
  const [familyMembers, setFamilyMembers] = useState([]);
  const [relations, setRelations] = useState([]);
  /* code -> account NAME, for "(02XXRE) Reckitt" on EVERY account option.
     /v1/patients/account/{pid} answers bare codes only (bridge prints them
     verbatim, which is why bridge's screen reads "04GEOL"), so the names come
     from ONE POST /v1/account/filter for exactly those codes, fetched as soon
     as the codes arrive (loadAccountNames). The plan rows' accountName is kept
     as a second source. Until a name is known the option reads the bare code. */
  const [accountNames, setAccountNames] = useState({});

  /* ONE label, and it has to be a function because the account field has TWO
     renderings: a Select when there are several, and a read-only line when
     there is one. Only the Select had the label at first, which meant the
     labelled form was almost never seen — a single account is the common case,
     and it is what every fixture stubs. Falls back to the bare code until the
     plan rows have supplied a name, so the field never reads "() ". */
  const accountLabel = (code) =>
    accountNames[code] ? `(${code}) ${accountNames[code]}` : code;
  const [memberSel, setMemberSel] = useState(null);

  /* "Select service provider or check mark to select automatically" — bridge's
     auto-select, and bridge's wording (navigo-module/CreateAppointment.js:3741-3772;
     the same string as phr's Reschedule dialog, by decision 2026-09-14). Ticked, no
     provider is chosen: slots are asked for with serviceProviderId 0, the server
     names the provider per date (`serviceProviderId` on the slots response,
     kept in autoSpId), and the price is that provider's row in the list already
     loaded. Only for the modes bridge allows it (MODES_WITH_AUTO_SP) — of phr's
     two bookable modes, that is Tele. */
  const [autoSp, setAutoSp] = useState(false);
  const [autoSpId, setAutoSpId] = useState(null);

  // In-person clinic (providerAddressId) picked from the provider's addresses[].
  const [clinicId, setClinicId] = useState(null);

  // Optional custom contact number for the appointment (tele/video only).
  const [customContactOn, setCustomContactOn] = useState(false);
  const [customContact, setCustomContact] = useState("");

  // Missing DOB/email backfill: { record, needDob, needEmail, then } | null.
  const [backfill, setBackfill] = useState(null);

  const [busy, setBusy] = useState("");
  const [errors, setErrors] = useState({});
  const [planBlocked, setPlanBlocked] = useState("");
  const [payload, setPayload] = useState(null);

  /**
   * Monotonic cascade generation. Bumped by every resetFrom; a loader captures
   * it at call time and drops its response if it has moved on.
   *
   * The service layer is callback-based with no abort, so this is the only way
   * to stop a stale response winning. It matters most for the provider call —
   * that response carries the price.
   */
  const gen = useRef(0);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  /** True when this response is still the one we asked for. */
  const fresh = (my) => mounted.current && my === gen.current;

  const showError = useCallback((message) => {
    EventService.emit("showError", { message });
  }, []);

  const fail = (field, message) => setErrors((e) => ({ ...e, [field]: message }));
  const clear = (field) =>
    setErrors((e) => {
      const n = { ...e };
      delete n[field];
      return n;
    });

  /**
   * Clear everything downstream of `step` — one linear reset, no 12-case switch.
   *
   * Also bumps `gen`, which invalidates every in-flight loader (see `fresh`).
   * Without that, a slow response for an old plan/mode lands after a newer one
   * and wins — and since the provider row carries the PRICE, that can quietly
   * turn a Rs 1000 booking into a "Free" one.
   */
  const resetFrom = useCallback((step) => {
    gen.current += 1;
    const i = STEPS.indexOf(step);
    if (i <= STEPS.indexOf("account")) {
      setAccounts([]);
      setAccount("");
    }
    if (i <= STEPS.indexOf("plan")) {
      setPlans([]);
      setPlanId(null);
    }
    if (i <= STEPS.indexOf("bookingfor")) setMemberSel(null);
    if (i <= STEPS.indexOf("service")) {
      setServices([]);
      setServiceId(null);
    }
    if (i <= STEPS.indexOf("mode")) setMode("");
    if (i <= STEPS.indexOf("date")) {
      setProvider(null);
      /* The LIST goes too, not just the pick. loadProvider clears these, but it
         only runs on a mode change — so without this a new patient inherits the
         previous patient's roster and pinned row, and the Provider select stays
         interactive against them. Rare before this screen went tenant-wide (it
         needed a doctor owning 2+ of their own rows); routine now. */
      setProviders([]);
      setProviderId(null);
      setDate("");
      // the clinic belongs to the selected provider — clear it when they change
      setClinicId(null);
      // bridge resets the auto-select tick with the provider (resetValues)
      setAutoSp(false);
      setAutoSpId(null);
    }
    if (i <= STEPS.indexOf("slot")) {
      setSlots([]);
      setSlot(null);
      setVendor({ vendorId: "", vendorCode: "", vendorDoctorId: 0, providerAddressId: "" });
    }
    if (i <= STEPS.indexOf("reason")) {
      setReasonId(null);
      setReasonDetails("");
    }
    setPlanBlocked("");
  }, []);


  // --- reasons master list, once
  useEffect(() => {
    let cancelled = false;
    apptService.getReasonsForConsultation(
      (res) => {
        if (cancelled) return;
        setReasons((res || []).filter((r) => r.isActive !== false));
      },
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, [apptService]);

  // --- relation labels (Booking-for), once
  useEffect(() => {
    let cancelled = false;
    service.getRelations(
      (res) => {
        if (cancelled) return;
        setRelations(Array.isArray(res) ? res : []);
      },
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, [service]);

  // --- who am I? Resolve the logged-in user to THEIR OWN provider row(s). This
  // runs for every session, employee or doctor: the booking is only ever made
  // with the logged-in user as the provider.
  //
  // An empty answer is NOT "let them pick anyone" — it means this account has no
  // provider identity (no doctorProfile.id, or no consultation provider carries
  // it), so it cannot create appointments. There is no employee→provider mapping
  // in the backend to fall back on. Fail closed in both empty and error cases.
  useEffect(() => {
    let cancelled = false;
    apptService.resolveMyServiceProviderIds(
      (ids) => {
        if (cancelled) return;
        setIdentity(ids && ids.length ? ids : "none");
      },
      () => {
        if (cancelled) return;
        setIdentity("error");
      }
    );
    return () => {
      cancelled = true;
    };
  }, [apptService]);

  // --- 1. patient, in two steps: find the number, then pick who on it.
  const searchPhone = () => {
    const q = String(phoneInput || "").replace(/[^0-9]/g, "");
    if (q.length < 6) return fail("patient", "Enter the patient's phone number.");
    // Never leave an id from a previous search sitting beside a new number.
    setPidInput("");
    clear("patient");
    setPatient(null);
    setProfiles(null);
    setProfilesPartial(false);
    resetFrom("account");
    setBusy("patient");
    const my = gen.current;

    patientService.searchByPhone(
      q,
      countryCode,
      (res) => {
        if (!fresh(my)) return;
        setBusy("");
        const list = (res && res.profiles) || [];
        setProfilesPartial(!!(res && res.partial));
        if (list.length === 0) {
          /* profiles STAYS null on a miss. `searched` is `profiles !== null`,
             and it is what opens up the Patient-name field and the other search
             box — so setting [] here answered a failed lookup by unfolding two
             empty, disabled fields underneath the error. Nothing was found;
             there is nothing to show but the message. */
          fail("patient", "No patient found on that number.");
          return;
        }
        setProfiles(list);
        /* ONE profile is not a choice — pick it. Making the operator confirm a
           list of one is friction with no decision in it. */
        if (list.length === 1) selectProfile(list[0]);
      },
      (err) => {
        if (!fresh(my)) return;
        setBusy("");
        fail("patient", (err && err.message) || "Couldn't search that number.");
      }
    );
  };

  /**
   * Editing the search box after a search throws away everything that search
   * produced — the patient, the profiles, and the whole account/plan/service/
   * provider/slot cascade behind them. Only the box being typed in survives.
   *
   * Without this the screen holds a resolved patient next to a number that no
   * longer identifies them, and every step below stays armed: you could change
   * one digit and still book the previous patient. `keep` says which box is
   * being edited so the OTHER one's echo goes too.
   *
   * resetFrom bumps `gen`, so an in-flight lookup is discarded rather than
   * landing on top of the edit — which is why this also fires while a search is
   * still running.
   */
  /**
   * Switching "Find patient by" RESTARTS THE FORM.
   *
   * This control is step 1: the patient is what every later step derives from —
   * account, plan, who it is booked for, service, mode, provider, date, slot.
   * Changing how we look the patient up abandons the search that produced all
   * of that, so leaving the cascade standing would let an operator book against
   * a patient they are no longer searching for. Both boxes go too, not just the
   * one being switched away from, because the other one holds an echo of the
   * same abandoned lookup (searchById fills the phone box, and vice versa).
   *
   * Guarded on an ACTUAL change. Without that, clicking the segment that is
   * already active — easy to do, it looks like a button — would silently throw
   * away a completed search.
   */
  const switchSearchBy = (next) => {
    if (next === searchBy) return;
    setSearchBy(next);
    setErrors({});
    setPatient(null);
    setProfiles(null);
    setProfilesPartial(false);
    setPhoneInput("");
    setPidInput("");
    /* Bumps `gen`, so a lookup still in flight for the old mode is discarded
       rather than landing on the freshly cleared form. */
    resetFrom("account");
  };

  const invalidateSearch = (keep) => {
    if (profiles === null && !patient && busy !== "patient") return;
    clear("patient");
    setPatient(null);
    setProfiles(null);
    setProfilesPartial(false);
    if (keep === "phone") setPidInput("");
    else setPhoneInput("");
    resetFrom("account");
  };

  /* The other way in: a patient id, straight to one person.
     
     Deliberately goes through the SAME /patients/search route as the phone
     search (type=1 instead of type=3) rather than GET /patients/{id}. The by-id
     read applies neither a tenant nor a status filter server-side, so it would
     let this screen start booking against a patient from another tenant or an
     inactive one. This route applies exactly the gate the phone route does.

     An id identifies exactly one patient, so there is no profile list to pick
     from — the single row is taken the same way a single phone result is. */
  const searchPatientId = () => {
    const q = String(pidInput || "").replace(/[^0-9]/g, "");
    if (!q || Number(q) <= 0) return fail("patient", "Enter the patient's ID.");
    // Never leave a number from a previous search sitting beside a new id.
    setPhoneInput("");
    clear("patient");
    setPatient(null);
    setProfiles(null);
    setProfilesPartial(false);
    resetFrom("account");
    setBusy("patient");
    const my = gen.current;

    patientService.searchById(
      q,
      countryCode,
      (res) => {
        if (!fresh(my)) return;
        setBusy("");
        const list = (res && res.profiles) || [];
        /* setProfiles even though an id resolves to exactly one row: the
           "Patient name" field renders from `profiles`, not from `patient`, so
           leaving it null showed a disabled select reading "Search a number
           first" next to a patient that had plainly been found. One row makes
           it a label rather than a choice. */
        if (list.length === 0) {
          /* Same as the phone route: leave `profiles` null so a miss shows the
             message alone, not the message plus two empty fields.

             "partial" matters more here than on the phone route: with one leg
             down, an id that exists can answer empty, and "no such patient" is
             then a definite answer built on half an answer. */
          fail(
            "patient",
            res && res.partial
              ? "Couldn't check that ID completely. Try again."
              : "No active patient with that ID."
          );
          return;
        }
        setProfiles(list);
        selectProfile(list[0]);
      },
      (err) => {
        if (!fresh(my)) return;
        setBusy("");
        fail("patient", (err && err.message) || "Couldn't search that ID.");
      }
    );
  };

  /* Picking a profile re-reads the FULL patient record rather than trusting the
     search row. The search answers a summary, and everything downstream — the
     account/plan cascade, the family list, the contact number — reads fields it
     does not carry. */
  const selectProfile = (row) => {
    const pid = Number(row && (row.patientId || row.id));
    if (!pid) return fail("patient", "That profile has no patient id.");
    clear("patient");
    setPatient(null);
    resetFrom("account");
    setBusy("patient");
    /* Read the generation AFTER resetFrom, which bumps it. Threading the
       caller's generation in was wrong and failed silently: the auto-select
       path passed a value captured before this reset, `fresh(my)` was then
       false, and the callback returned early — leaving `busy` stuck on
       "patient", which disabled the search button for every later lookup. */
    const my = gen.current;

    patientService.fetchPatient(
      pid,
      (res) => {
        if (!fresh(my)) return;
        setBusy("");
        /* Bridge tests `res.id != 3`, which is a bug it inherited: 3 is
           PatientStatusInactive (api-user-service/models/patient.go:17) — a
           STATUS, not an id. As written it matched patient #3 and nothing else,
           so it blocked no inactive patient and would have refused a legitimate
           one whose id happened to be 3.

           Test the field that says it, and refuse only the two statuses that
           mean "not a bookable person": Blocked(2) and Inactive(3). The other
           eleven are lifecycle states — Pending, Renewal due, On trial and so
           on — and refusing those would block real work.

           Liberal when `status` is absent: both search routes already gate on
           it server-side, so this is defence in depth, not the gate. */
        const NOT_BOOKABLE = [2, 3];
        if (!res || !res.id) {
          return fail("patient", "No active patient with that ID.");
        }
        if (res.status != null && NOT_BOOKABLE.indexOf(Number(res.status)) >= 0) {
          return fail("patient", "That patient is blocked or inactive, so they can't be booked.");
        }
        setPatient(res);
        /* Both routes converge here, so the two boxes show the resolved patient
           whichever way they were found: the id route fills the phone, the
           phone route fills the id (and re-normalises what was typed). */
        setPidInput(String(res.id));
        if (res.phoneno) setPhoneInput(String(res.phoneno));
        loadFamily(res, my);
        loadAccounts(pid, my);
      },
      (err) => {
        if (!fresh(my)) return;
        setBusy("");
        fail("patient", (err && err.message) || "Couldn't fetch that patient.");
      }
    );
  };

  // --- Booking-for list: the patient themself (relation 0 / Self) at index 0,
  // then their family. A failed family fetch is fine — self-only still works.
  const selfRow = (p) => ({
    memberPatientId: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    gender: p.gender,
    /* Carried so the booking-for label can show an age for the patient too.
       The family rows come straight off /v1/family/{id}?includeExtended=true,
       which supplies dateOfBirth; this row is built by hand and used to drop
       it, so "Self" was the one option with a blank age slot. */
    dateOfBirth: p.dateOfBirth,
    age: p.age,
    relation: 0,
  });
  const loadFamily = (p, my) => {
    setFamilyMembers([selfRow(p)]);
    service.getFamilyMembers(
      p.id,
      (res) => {
        // Drop a late response for a patient the operator has moved off — else
        // patient B could end up showing patient A's family.
        if (!fresh(my)) return;
        const fam = ((res && res.families) || []).filter(
          (m) => m && m.memberPatientId && m.memberPatientId !== p.id
        );
        setFamilyMembers([selfRow(p), ...fam]);
      },
      () => {}
    );
  };

  /**
   * Choose who the appointment is for. Verifies the person's demographics
   * (backfilling DOB/email via the dialog when absent), then loads the services
   * available to them. `planArg` covers the case where planId was just set and
   * its state update hasn't flushed yet.
   */
  const chooseMember = (member, planArg) => {
    const pl = planArg != null ? planArg : planId;
    setMemberSel(member);
    resetFrom("service");
    const my = gen.current;
    const mpid = member.memberPatientId;
    const proceed = () => loadServices(patient.id, pl, mpid, my);
    const gate = (rec) => {
      if (isDobMissing(rec) || isEmailMissing(rec)) {
        setBackfill({
          record: rec,
          needDob: isDobMissing(rec),
          needEmail: isEmailMissing(rec),
          then: proceed,
        });
      } else {
        proceed();
      }
    };
    if (mpid === patient.id) {
      gate(patient); // self — we already hold the full record
      return;
    }
    setBusy("member");
    patientService.fetchPatient(
      mpid,
      (full) => {
        if (!fresh(my)) return;
        setBusy("");
        if (!full || !full.id || full.id === 3) {
          showError("That member is inactive.");
          return;
        }
        gate(full);
      },
      (err) => {
        if (!fresh(my)) return;
        setBusy("");
        showError((err && err.message) || "Couldn't load that member.");
      }
    );
  };

  // --- 2. accounts
  const loadAccounts = (pid, my) => {
    setBusy("account");
    service.getAccountsForPid(
      pid,
      (res) => {
        if (!fresh(my)) return;
        setBusy("");
        const codes = (res && res.accountCodes) || [];
        setAccounts(codes);
        if (codes.length) loadAccountNames(codes, my);
        if (codes.length === 1) {
          setAccount(codes[0]);
          loadPlans(pid, codes[0], my);
        }
      },
      (err) => {
        if (!fresh(my)) return;
        setBusy("");
        showError(`Couldn't load accounts: ${(err && err.message) || ""}`);
      }
    );
  };

  /* The NAME of every account on offer, in one request, so each option reads
     "(02XXRE) Reckitt" before anything is picked. Fails soft: on an error the
     options keep their codes, which is what they showed before. */
  const loadAccountNames = (codes, my) => {
    service.getAccountsByCodes(
      codes,
      (res) => {
        if (!fresh(my)) return;
        const rows = (res && res.accounts) || [];
        setAccountNames((prev) => {
          const next = { ...prev };
          rows.forEach((a) => {
            if (a && a.accountCode && a.accountName) next[a.accountCode] = a.accountName;
          });
          return next;
        });
      },
      () => {}
    );
  };

  // --- 3. plans
  const loadPlans = (pid, code, my) => {
    setBusy("plan");
    service.getPlansForPid(
      pid,
      (res) => {
        if (!fresh(my)) return;
        setBusy("");
        const all = Array.isArray(res) ? res : (res && res.plans) || [];
        /* Harvest every account's name on the way past — the response is the
           patient's WHOLE plan list, not just this account's, so one pass names
           all of them. Only rows that actually carry a name contribute; a
           missing one leaves the label as the bare code rather than "() ". */
        setAccountNames((prev) => {
          const next = { ...prev };
          all.forEach((p) => {
            const c = p && p.accountCode;
            const nm = p && p.accountName;
            if (c && nm && !next[c]) next[c] = nm;
          });
          return next;
        });
        // `status` is the plan TEMPLATE's and is 1 for every row, so it filters
        // nothing. bookConsultation is the only meaningful client-side signal;
        // real bookability is only known once the provider call answers.
        setPlans(all.filter((p) => p.accountCode === code && p.bookConsultation));
      },
      (err) => {
        if (!fresh(my)) return;
        setBusy("");
        showError(`Couldn't load plans: ${(err && err.message) || ""}`);
      }
    );
  };

  // --- 4. services
  const loadServices = (pid, plan, mpid, my) => {
    setBusy("service");
    service.getServicesForPatient(
      {
        pageNum: 0,
        pageSize: 0,
        planId: plan,
        serviceType: 1,
        patientId: pid,
        // Whoever the appointment is FOR — self or a family member.
        memberPatientId: mpid,
        includeSubtypeIds: [],
        excludeSubtypeIds: [],
      },
      (res) => {
        if (!fresh(my)) return;
        setBusy("");
        const list = ((res && res.services) || []).filter((s) =>
          (s.serviceModeDetails || []).some((m) => isSupportedMode(m.modeName))
        );
        setServices(list);
        if (!list.length) {
          setPlanBlocked(
            "This plan has no consultation services available to book."
          );
        }
      },
      (err) => {
        if (!fresh(my)) return;
        setBusy("");
        // A plan the patient isn't actively enrolled in answers here too.
        setPlanBlocked(planMessage(err));
      }
    );
  };

  /** The server's "not bookable" answers are expected, not exceptional. */
  const planMessage = (err) => {
    const m = (err && err.message) || "";
    if (/no active plan/i.test(m)) {
      return "This plan isn't active for this patient, so it can't be booked. Pick another plan.";
    }
    return m || "This plan can't be booked right now.";
  };

  // --- 5. provider + PRICE (the money-critical call)
  //
  // The staleness guard here is not defensive padding: this response carries the
  // price. Switch mode twice quickly and, unguarded, the slower first response
  // lands last and pins the WRONG mode's price — a Rs 1000 consultation showing
  // (and booking) as Free.
  const loadProvider = (pid, plan, svc, m, mpid, my) => {
    setBusy("provider");
    setProvider(null);
    setProviders([]);
    setProviderId(null);
    service.getServiceProviderForPatient(
      {
        pageNum: 0,
        pageSize: 0,
        planId: plan,
        serviceId: svc,
        serviceMode: m,
        patientId: pid,
        memberPatientId: mpid,
        // [] means EVERY provider offering this plan/service/mode. That is
        // deliberate now (asked for 2026-09-03): an operator books with whoever
        // is right, not only themselves. Bridge's ops screen has always sent
        // this. The price still follows the SELECTED row, so widening the list
        // widens the choice, never the charge.
        serviceProviderIds: [],
        languageMatch: [],
        locationFilter: [],
        geolocationFilter: null,
      },
      (res) => {
        if (!fresh(my)) return;
        setBusy("");
        const list = (res && res.providers) || [];
        // Drop id 0: the transform pre-sizes this array and an excluded provider
        // leaves a zero-value row (id 0, price 0). Letting one into the picker
        // would book a paid consultation as free.
        //
        // `id > 0` is the MONEY guard and is all that is left here — it was
        // fused with an isMine() check, and dropping the wrong half of this one
        // line is how a price-0 placeholder reaches the picker.
        const bookable = list.filter((p) => p && p.id > 0);
        if (!bookable.length) {
          setPlanBlocked(
            "No provider offers this plan and service in this mode. Pick another service or mode."
          );
          return;
        }
        setProviders(bookable);
        /* One row, no choice to make. The other auto-pick — the operator's own
           row, further down where sortedProviders is built — is the ONLY case
           in which a provider is chosen for them out of several, and it was
           asked for (product, 2026-09-10) precisely because it is their own.
           Any OTHER row still has to be picked by hand, so a price the operator
           did not choose never lands on screen. */
        if (bookable.length === 1) {
          setProviderId(bookable[0].id);
          setProvider(bookable[0]);
        }
      },
      (err) => {
        if (!fresh(my)) return;
        setBusy("");
        setPlanBlocked(planMessage(err));
      }
    );
  };

  /* The operator's own rows first. With a tenant-wide list the "(you)" suffix is
     easy to miss halfway down a scroll, so the common case — booking with
     yourself — stays one click away.

     SORTED AT RENDER, not where the list arrives. `identityReady` used to gate
     the mode Select, which guaranteed identity had resolved before loadProvider
     could run; removing that gate means the provider response can now land
     first. Sorting at fetch time would then see isMine() false for every row,
     produce a no-op, and never re-run — `providers` is written once and nothing
     watches it — while the "(you)" labels appeared anyway on the next render.
     A memo keyed on isMine re-orders exactly when identity arrives. V8's sort is
     stable, so the server's order survives inside each group. */
  const sortedProviders = useMemo(
    () => [...providers].sort((a, b) => (isMine(b.id) ? 1 : 0) - (isMine(a.id) ? 1 : 0)),
    [providers, isMine]
  );

  /* PRE-SELECT THE OPERATOR'S OWN PROVIDER ROW (product, 2026-09-10).
     Booking with yourself is the common case, so if the logged-in provider is
     among the options, take them. If they are NOT among them, nothing is
     pre-selected — the operator chooses, exactly as before.

     AN EFFECT, NOT A LINE IN loadProvider's CALLBACK, and that is the whole
     difficulty. Identity and the provider list are two independent requests and
     either can land first: `identityReady` used to gate the mode Select, which
     guaranteed identity had resolved before loadProvider could run, and removing
     that gate means the roster can arrive first. A pre-select written where the
     list arrives would run isMine() against a still-loading identity, match
     nothing, and never re-run — `providers` is written once and nothing watches
     it.

     WHAT ACTUALLY MAKES THIS RE-RUN is reading `sortedProviders`: it is memoised
     on `[providers, isMine]`, so identity landing rebuilds it and this effect
     sees a new array. `isMine` is in the deps below because the callback calls
     it, not because it is what carries the timing — dropping it from the deps
     still passes the late-identity assertion, which is worth knowing before
     anyone "simplifies" the memo away and takes the timing with it.

     GUARDED ON `providerId == null`, which is what stops it fighting the
     operator: pick someone else while identity is still resolving and this must
     not overwrite that choice a moment later. It also means the existing
     single-provider auto-pick wins first when there is only one row, which is
     the same outcome by a shorter path.

     `sortedProviders`, so the row taken is the one shown at the top of the list
     when a provider owns more than one. Deliberately does NOT call resetFrom:
     nothing downstream can have been chosen yet (providerId was null), and
     resetFrom("date") would clear the very roster being read. */
  useEffect(() => {
    // Ticked "choose automatically" means NO provider — never re-pin one.
    if (autoSp || providerId != null || !sortedProviders.length) return;
    const mine = sortedProviders.find((p) => isMine(p.id));
    if (!mine) return;
    setProviderId(mine.id);
    setProvider(mine);
  }, [sortedProviders, isMine, providerId, autoSp]);

  // --- in-person: where the visit is (see the header). Only a row that brings
  // a list of clinics (`addresses[]`) asks for a pick; the usual row has its
  // one `address` and needs nothing chosen.
  const clinics = (provider && provider.addresses) || [];
  const pickClinic = clinics.length > 0;
  const clinicReady = !pickClinic || !!clinicId;

  // --- 6. slots
  const memberPatientId = memberSel ? memberSel.memberPatientId : patient && patient.id;
  useEffect(() => {
    if (!patient || !planId || !serviceId || !mode || !date || (!providerId && !autoSp)) return undefined;
    // In-person with clinics to choose from needs one picked first — it drives
    // which slots exist.
    if (isInPersonMode(mode) && !clinicReady) return undefined;
    let cancelled = false;
    setBusy("slots");
    setSlots([]);
    setSlot(null);
    setAutoSpId(null);

    // For in-person the server reads the vendor + clinic fields; send them from
    // the selected provider/clinic (they come back echoed for the create body).
    const inPerson = isInPersonMode(mode);
    service.getAvailableSlots(
      {
        planId,
        serviceId,
        patientId: patient.id,
        // 0 = bridge's auto-select: the server picks the provider for this date.
        serviceProviderId: autoSp ? 0 : providerId,
        bookingMode: mode,
        collectionDate: date,
        addressId: null,
        vendor: inPerson && provider ? provider.vendorId || "" : "",
        vendorCode: inPerson && provider ? provider.vendorCode || "" : "",
        vendorDoctorId: inPerson && provider ? provider.vendorDoctorId || 0 : 0,
        // Legacy: `serviceProviderSelected.address?.providerAddressId ?? ""`.
        providerAddressId: inPerson
          ? (pickClinic
              ? clinicId
              : (provider && provider.address && provider.address.providerAddressId) || "")
          : "",
        locationFilter: [],
        languageMatch: [],
        memberPatientId,
      },
      (res) => {
        // Guard a late response for a date the user has already moved off.
        if (cancelled) return;
        setBusy("");
        setSlots((res && res.slots) || []);
        // Auto-select: who the server chose for THIS date (bridge B:790-794).
        setAutoSpId(autoSp ? (res && res.serviceProviderId) || null : null);
        // The four vendor fields the create payload must echo. Empty for
        // tele/video, which is correct — the server only reads them in person.
        // NB vendorName -> vendorCode is the real contract, not a typo.
        setVendor({
          vendorId: (res && res.vendorId) || "",
          vendorCode: (res && res.vendorName) || "",
          vendorDoctorId: (res && res.vendorDoctorId) || 0,
          providerAddressId: (res && res.providerAddressId) || "",
        });
      },
      (err) => {
        if (cancelled) return;
        setBusy("");
        showError(`Couldn't load slots: ${(err && err.message) || ""}`);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [patient, planId, serviceId, mode, date, providerId, clinicId, pickClinic, clinicReady, provider, memberPatientId, service, showError, autoSp]);

  /* With auto-select on, the provider is whoever the chosen date's slots
     response named, and its price is that row's in the list already loaded
     (bridge, navigo-module/CreateAppointment.js:348-366). An id the list does
     not hold leaves the price unknown, so the form stays not-ready — bridge
     would get stuck there instead. */
  const autoProvider =
    autoSp && autoSpId ? providers.find((p) => p.id === autoSpId) || null : null;
  const effectiveProvider = autoSp ? autoProvider : provider;
  const effectiveProviderId = effectiveProvider ? effectiveProvider.id : null;
  const price = effectiveProvider ? effectiveProvider.price : null;
  const priceKnown = price !== null && price !== undefined && !isNaN(price) && price >= 0;

  const inPerson = isInPersonMode(mode);
  // 0 when booking for the patient themself (relation Self), else the member id.
  const bookedFor = memberSel && memberSel.memberPatientId !== (patient && patient.id)
    ? memberSel.memberPatientId
    : 0;
  // Custom contact is a tele/video field; it never gates or reaches an in-person
  // booking (which hides it entirely).
  const useCustomContact = !inPerson && customContactOn;
  const contactValid = inPerson || !customContactOn || /^\d{10}$/.test(customContact.trim());

  const ready =
    !!patient &&
    !!account &&
    !!planId &&
    !!memberSel &&
    !!serviceId &&
    !!mode &&
    !!date &&
    !!slot &&
    !!effectiveProviderId &&
    priceKnown &&
    !planBlocked &&
    contactValid &&
    // In-person needs no reason (and a clinic only when there are several to
    // choose from); tele/video need a reason (with details when "Other").
    (inPerson
      ? clinicReady
      : !!reasonId && (reasonId !== REASON_OTHER || !!reasonDetails.trim()));

  const submit = () => {
    if (!ready) return;
    if (!inPerson && reasonId === REASON_OTHER && !reasonDetails.trim()) {
      return fail("reason", "Describe the reason.");
    }
    const body = {
      // Local date parts + a fixed midnight suffix. toISOString() would shift
      // the day for any non-UTC user and book the wrong date.
      appointmentDate: `${date}T00:00:00.000Z`,
      bookedFor,
      patientID: patient.id,
      serviceID: serviceId,
      timeSlot: slot.value,
      timeSlotId: slot.key,
      planID: planId,
      mode,
      serviceProviderID: effectiveProviderId,
      isCustomContactNumber: useCustomContact,
      customContactNumber: useCustomContact
        ? { countryCode: Number(patient.countryCode) || 0, phoneNo: Number(customContact.trim()) }
        : { countryCode: 0, phoneNo: 0 },
      reportURL: "",
      // Bridge sends no reason for in-person and skips its validation.
      reasonId: inPerson ? 0 : reasonId || 0,
      reasonDetails: inPerson ? "" : reasonDetails,
      vendorId: vendor.vendorId,
      vendorCode: vendor.vendorCode,
      vendorDoctorId: vendor.vendorDoctorId,
      // A picked clinic when there was a list; otherwise the slots' echo, as
      // legacy sends for every mode (tele echoes back "").
      providerAddressId: inPerson && pickClinic ? clinicId : vendor.providerAddressId,
    };
    // Both the free and paid paths run through the dialog; it decides which.
    setPayload(body);
  };

  const selectedService = services.find((s) => (s.serviceId || s.id) === serviceId);

  /* The bookable modes OF A GIVEN SERVICE. Pulled out of the render body so the
     same filter answers two questions: what the mode field lists, and — at the
     moment a service is picked, before `serviceId` state has caught up — whether
     there is only one mode to pick. */
  const modesOf = (svc) =>
    ((svc && svc.serviceModeDetails) || []).map((m) => m.modeName).filter(isSupportedMode);

  const modes = modesOf(selectedService);

  /* Choosing a mode, from the field or automatically — ONE function, because
     picking a mode is not just setMode: it drops the custom contact (a tele
     concern), resets the steps below, and kicks off the provider load. An
     auto-pick that only called setMode would leave the cascade stalled with no
     providers and no visible reason why.

     `svcArg` exists for the same reason chooseMember takes `planArg`: when this
     runs from chooseService below, `serviceId` state is still the PREVIOUS
     service, and loadProvider would ask for the wrong one. */
  const chooseMode = (m, svcArg) => {
    const svc = svcArg != null ? svcArg : serviceId;
    setMode(m);
    // Custom contact is a tele/video concern — a mode switch drops it.
    setCustomContactOn(false);
    setCustomContact("");
    resetFrom("date");
    loadProvider(patient.id, planId, svc, m, memberPatientId, gen.current);
  };

  /* ONE MODE IS NOT A CHOICE (product, 2026-09-10). When the picked service
     offers exactly one bookable mode, take it and move on rather than asking the
     operator to confirm the only option — the same rule the account and provider
     steps already follow (loadAccounts, loadProvider). Two or more and it stays
     a decision, so the mode on the booking is always one somebody chose.

     Note this is now MORE likely to fire than it looks: the mode list is
     filtered to the bookable modes, so a service offering Tele + Video has one
     mode left, not two. */
  const chooseService = (id, svc) => {
    setServiceId(id);
    resetFrom("mode");
    const only = modesOf(svc);
    if (only.length === 1) chooseMode(only[0], id);
  };

  /* ── what is on screen right now ─────────────────────────────────────
     Hoisted out of the JSX so each of the four section headings can be gated on
     the SAME conditions as the fields inside it: a heading with nothing under
     it is worse than no heading at all. Every line here is the condition that
     already guarded that field — none of them is new, and none of them decides
     anything the cascade did not already decide. */
  const hints = loadingHints({ busy, profiles, patient, searchBy, planBlocked });
  const showAccount = accountVisible(patient, accounts, hints.account);
  const showPlan = !!account;
  const showBookingFor = !!planId && familyMembers.length > 1;
  const showService = !!memberSel;
  const showMode = !!serviceId;
  const showPlanSection =
    showAccount || showPlan || showBookingFor || showService || showMode || !!planBlocked;

  // Bridge's MODES_WITH_AUTO_SP, compared the way every mode here is (normMode).
  const autoSpAllowed = MODES_WITH_AUTO_SP.some((x) => normMode(x) === normMode(mode));
  /* Ticked, the picker stays on screen (disabled, empty) even for a one-row
     list — the field must not vanish because nobody is chosen. */
  const showProviderPick = (providers.length > 1 || autoSp) && !planBlocked;
  const showProviderOnly = !!provider && providers.length === 1 && !autoSp && !planBlocked;
  const showClinic = !!provider && inPerson && !planBlocked;
  /* identity no longer gates this section: it is a LABEL now ("(you)"), not a
     permission. An account with no provider record of its own books like any
     other operator. */
  /* …and while the providers LOAD: after a mode was chosen the section used to
     stay absent until they arrived, so the screen looked stuck (2026-09-21). */
  const showProviderSection = anyOf(showProviderPick, showProviderOnly, showClinic, hints.provider);
  const providerRow = anyOf(showProviderPick, showProviderOnly, hints.provider);

  const showDate = (!!provider || autoSp) && !planBlocked && (!inPerson || clinicReady);
  const showSlot = !!date && !planBlocked;
  const showTotal = !!effectiveProvider && priceKnown && !planBlocked;
  const showContactAndReason = !!slot && !inPerson;
  const showReasonDetails = !inPerson && reasonId === REASON_OTHER;
  const showScheduleSection =
    showDate || showSlot || showTotal || showContactAndReason || showReasonDetails;

  /* Bridge's "Total amount": `Rs ${aptPrice}`, so a plan-covered booking reads
     "Rs 0" (decision 2026-09-14: follow bridge for wordings). */
  const priceLabel = rupees(price) || "Rs 0";

  /* Bridge's checkbox handler (B:3755-3766): the date, slot and provider go —
     ticked, nobody is chosen. Unticking a one-row list puts that row back, the
     same auto-pick loadProvider made, so the read-only line has someone to show;
     with several rows the operator's own is re-picked by the effect above. */
  const toggleAutoSp = () => {
    const next = !autoSp;
    setDate("");
    setSlot(null);
    setSlots([]);
    setVendor({ vendorId: "", vendorCode: "", vendorDoctorId: 0, providerAddressId: "" });
    setAutoSpId(null);
    setClinicId(null);
    if (!next && providers.length === 1) {
      setProviderId(providers[0].id);
      setProvider(providers[0]);
    } else {
      setProviderId(null);
      setProvider(null);
    }
    setAutoSp(next);
  };

  /* Read-only patient facts (3041:10011, 3041:10040). The age is DERIVED from
     the date of birth and never read off the stored `age` column: the two are
     independent columns with no derivation between them, and the number is the
     one that goes stale. Same rule as the patient header. */
  /* Has a search actually RUN? Both routes set `profiles` (the id route to a
     one-row list), and both null it again when a new search starts — so this is
     also what re-hides the fields while the next lookup is in flight.

     Drives progressive disclosure: only the box you are searching from is on
     screen until then. Showing an empty "Patient ID" beside the phone field
     invited the operator to type in a box that was not the one being searched,
     which is exactly what the mode switch exists to settle. */
  const searched = profiles !== null;

  const genderLabel = !patient
    ? ""
    : patient.gender === Gender.FEMALE
    ? "Female"
    : patient.gender === Gender.MALE
    ? "Male"
    : "";
  const ageLabel = patient ? formatAgeLabel(patient.dateOfBirth, patient.age) : "";

  const goBack = () => history.push("/navigo-module");


  return (
    <div className="create-page">
      {/* The board splits the old single bar in two: a breadcrumb strip
          (3041:9964), and the page title with Cancel beside it INSIDE the
          scrolling content (3041:9972). */}
      <header className="create-topbar">
        <button
          type="button"
          className="back-btn"
          aria-label="Back to appointments"
          onClick={goBack}
        >
          &#8249;
        </button>
        <nav className="create-crumbs" aria-label="Breadcrumb">
          <button type="button" className="crumb-link" onClick={goBack}>
            My appointments
          </button>
          <span className="crumb-sep" aria-hidden="true">/</span>
          <span className="crumb-here" aria-current="page">Create appointment</span>
        </nav>
      </header>

      <div className="create-body">
        <div className="create-titlerow">
          <h1>Create appointment</h1>
          <button type="button" className="zy-btn zy-btn-ghost" onClick={goBack}>
            Cancel
          </button>
        </div>

        <div className="create-card">
          {/* ─────────── Patient details (3041:9977) ─────────── */}
          <section className="cf-section">
            <h2 className="cf-section-title">Patient details</h2>

            {/* TWO ways in. By PHONE, then chosen from the profiles on that
                number; or by PATIENT ID, straight to one person. The idle field
                stays visible as the OUTPUT it always was (3041:10004) — both
                routes fill both boxes. */}
            <div className="cf-searchby" role="group" aria-label="Find the patient by">
              <span className="cf-searchby-label">Find patient by</span>
              <div className="cf-seg">
                <button
                  type="button"
                  className={`cf-seg-btn${searchBy === "phone" ? " is-on" : ""}`}
                  aria-pressed={searchBy === "phone"}
                  onClick={() => switchSearchBy("phone")}
                >
                  Phone number
                </button>
                <button
                  type="button"
                  className={`cf-seg-btn${searchBy === "pid" ? " is-on" : ""}`}
                  aria-pressed={searchBy === "pid"}
                  onClick={() => switchSearchBy("pid")}
                >
                  Patient ID
                </button>
              </div>
            </div>

            <div className="cf-row cf-row-3">
              {(searchBy === "phone" || searched) && (
              <div className={`cf-field cf-ord-${searchBy === "phone" ? "1" : "3"}`}>
                <label htmlFor="phone">
                  Phone number<span className="cf-req">*</span>
                </label>
                <div className={`cf-phone${searchBy === "phone" ? "" : " is-idle"}`}>
                  <span className="cf-cc">+{countryCode}</span>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="off"
                    /* Ten X, not a specimen number: "9413675432" read as a value
                       already typed in rather than as a hint. */
                    placeholder="XXXXXXXXXX"
                    readOnly={searchBy !== "phone"}
                    /* Read-only (a Patient-ID lookup filled it), it only SHOWS
                       the found patient's number — masked. Typed, it is the
                       operator's own search and stays as typed. */
                    value={searchBy === "phone" ? phoneInput : maskPhone(null, phoneInput)}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                      setPhoneInput(v);
                      invalidateSearch("phone");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && searchBy === "phone" && searchPhone()}
                  />
                  {searchBy === "phone" && (
                    <button
                      type="button"
                      className="cf-phone-search"
                      aria-label="Search this number"
                      disabled={busy === "patient"}
                      onClick={searchPhone}
                    >
                      <ArrowIcon />
                    </button>
                  )}
                </div>
                {/* One error line, under whichever box is doing the searching —
                    both routes write the same `patient` key. */}
                {errors.patient && searchBy === "phone" && (
                  <p className="cf-error">{errors.patient}</p>
                )}
                <LoadingNote show={hints.searchPhone} text="Searching…" />
              </div>
              )}

              {searched && (
              <div className="cf-field cf-ord-2">
                <label htmlFor="profile">
                  Patient name<span className="cf-req">*</span>
                </label>
                {/* A SELECT, not a list of cards: the board draws one field
                    (3041:9999) and most numbers carry one or two people. It is
                    disabled until a search has actually returned something, so
                    an empty dropdown never invites a click. */}
                <select
                  id="profile"
                  className="cf-select"
                  disabled={!profiles || profiles.length === 0 || busy === "patient"}
                  value={patient ? String(patient.id) : ""}
                  onChange={(e) => {
                    const row = (profiles || []).find(
                      (r) => String(r.patientId || r.id) === e.target.value
                    );
                    if (row) selectProfile(row);
                  }}
                >
                  <option value="" disabled>
                    {/* Copy follows the SWITCH: telling someone to "search a
                        number first" while they are searching by patient id is
                        instructions for a control they are not using. */}
                    {!profiles
                      ? searchBy === "pid"
                        ? "Search an ID first"
                        : "Search a number first"
                      : profiles.length === 0
                      ? searchBy === "pid"
                        ? "No patient with this ID"
                        : "No patient on this number"
                      : "Select a profile"}
                  </option>
                  {(profiles || []).map((r) => {
                    const id = String(r.patientId || r.id);
                    const name =
                      [r.firstName, r.lastName].filter(Boolean).join(" ") || r.name || `Patient ${id}`;
                    return (
                      <option key={id} value={id}>
                        {name} · {id}
                      </option>
                    );
                  })}
                </select>
                {profilesPartial && (
                  <p className="cf-error cf-warn">
                    Some profiles couldn&rsquo;t be loaded — this list may be incomplete.
                  </p>
                )}
                <LoadingNote show={hints.record} text="Loading patient details…" />
              </div>
              )}

              {(searchBy === "pid" || searched) && (
              <div className={`cf-field cf-ord-${searchBy === "pid" ? "1" : "3"}`}>
                <label htmlFor="pid">
                  Patient ID<span className="cf-req">*</span>
                </label>
                {/* Searchable, and still the echo: a phone search writes the
                    resolved id here, and typing one and pressing the arrow
                    looks that patient up directly. Same box either way, so the
                    operator is never asked which mode they are in. */}
                {/* Same box as the phone field — it has no .cf-cc prefix, and
                    .cf-phone already pads 12px on the left, so it needs no rule
                    of its own. */}
                <div className={`cf-phone${searchBy === "pid" ? "" : " is-idle"}`}>
                  <input
                    id="pid"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="XXXXXXXX"
                    readOnly={searchBy !== "pid"}
                    value={pidInput}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 12);
                      setPidInput(v);
                      invalidateSearch("pid");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && searchBy === "pid" && searchPatientId()}
                  />
                  {searchBy === "pid" && (
                    <button
                      type="button"
                      className="cf-phone-search"
                      aria-label="Find patient by ID"
                      disabled={busy === "patient"}
                      onClick={searchPatientId}
                    >
                      <ArrowIcon />
                    </button>
                  )}
                </div>
                {errors.patient && searchBy === "pid" && (
                  <p className="cf-error">{errors.patient}</p>
                )}
                <LoadingNote show={hints.searchPid} text="Searching…" />
              </div>
              )}
            </div>

            {/* The rest of the record, read-only (3041:10011 + 3041:10040).
                These replace the old summary card: same facts, laid out as the
                fields they are, and each one of them is already in hand — none
                of this costs an extra call. */}
            {patient && (
              <>
                {/* Still cf-row-3 with the last column empty, so these two keep
                    the width the board gave them. Tenant used to sit first: it
                    was a LABEL for a value phr never lets you change (the login
                    pins tenant 1, there is no picker), and the server ignores a
                    client-supplied one anyway — appointment_controller.go:67
                    overwrites req.TenantID from the token straight after bind. */}
                {/* THREE-UP, both rows (product, 2026-09-10). Email joins Date of
                    birth and Gender as the third column, and the age drops to the
                    first column of the next row so it sits under Date of birth at
                    the same width — a third, not the quarter it used to be.

                    This SUPERSEDES the board's four-up Email/age split
                    (3041:10048, 3041:10049), which put Email at a quarter width
                    with two empty middle columns and pushed the age to the far
                    right, diagonally opposite the date it is derived from. */}
                <div className="cf-row cf-row-3">
                  <ReadField label="Date of birth" required value={formatDob(patient.dateOfBirth)} />
                  <ReadField label="Gender" required value={genderLabel} />
                  <ReadField label="Email ID" required value={patient.email} />
                </div>
                <div className="cf-row cf-row-3">
                  <ReadField label="Patient current age" required value={ageLabel} />
                </div>
              </>
            )}
          </section>

          {/* ─────────── Plan & service (3041:10059) ─────────── */}
          {showPlanSection && (
            <section className="cf-section">
              <h2 className="cf-section-title">Plan &amp; service</h2>

              <div className="cf-row cf-row-3">
                {showAccount && (
                  <div className="cf-field">
                    <label htmlFor="account">
                      Select account<span className="cf-req">*</span>
                    </label>
                    {accounts.length === 1 ? (
                      <div className="cf-readonly">{accountLabel(accounts[0])}</div>
                    ) : (
                      <Select
                        id="account"
                        searchable
                        value={account}
                        options={accounts}
                        getKey={(a) => a}
                        /* "(02XXRE) Reckitt" when the name is known, the bare
                           code until it is. NOT bridge parity: bridge prints the
                           raw code here (its getOptionLabel is the identity, and
                           the endpoint answers bare codes). Asked for
                           2026-09-10. */
                        getLabel={accountLabel}
                        placeholder={loadingOr(hints.account, "Select an account")}
                        disabled={hints.account}
                        onChange={(code) => {
                          setAccount(code);
                          resetFrom("plan");
                          // gen was just bumped by resetFrom — capture it AFTER, so
                          // this loader is the current generation, not the old one.
                          loadPlans(patient.id, code, gen.current);
                        }}
                      />
                    )}
                  </div>
                )}

                {showPlan && (
                  <div className="cf-field">
                    <label htmlFor="plan">
                      Select plan<span className="cf-req">*</span>
                    </label>
                    <Select
                      id="plan"
                      searchable
                      value={planId}
                      options={plans}
                      getKey={(p) => p.planId || p.id}
                      /* "(21465) Health suraksha" — bridge's exact shape
                         (navigo-module/CreateAppointment.js:2818). `planId` is
                         the id every downstream call keys on, not a display
                         number. */
                      getLabel={(p) => `(${p.planId || p.id}) ${p.name || p.planName}`}
                      placeholder={busy === "plan" ? "Loading…" : "Select a plan"}
                      disabled={busy === "plan"}
                      onChange={(id) => {
                        setPlanId(id);
                        resetFrom("bookingfor");
                        // Default booking-for to the patient themself and load their
                        // services; the operator can switch to a family member below.
                        chooseMember(familyMembers[0] || selfRow(patient), id);
                      }}
                    />
                    {!plans.length && busy !== "plan" && (
                      <p className="cf-note">
                        No plans on this account allow consultation booking.
                      </p>
                    )}
                  </div>
                )}

              </div>

              {/* THE SERVICE ON A ROW OF ITS OWN, full width, as bridge lays it out
                  (navigo-module/CreateAppointment.js, Col lg={11}). Its label is
                  bridge's long "(id) code / name (modes) (price)", which a
                  one-third column cut off mid-word. */}
              {showService && (
                <div className="cf-row cf-row-1">
                {/* Deliberately NOT gated on !planBlocked: a provider failure
                    blocks on the service/mode combination, and hiding these two
                    selects would leave no way to pick a different one. resetFrom
                    clears planBlocked, so changing either retries. */}
                  <div className="cf-field">
                    <label htmlFor="service">
                      Select service code/name<span className="cf-req">*</span>
                    </label>
                    <Select
                      id="service"
                      searchable
                      value={serviceId}
                      options={services}
                      getKey={(s) => s.serviceId || s.id}
                      getLabel={serviceLabel}
                      placeholder={loadingOr(hints.service, "Select a service")}
                      disabled={hints.service}
                      onChange={(id, svc) => chooseService(id, svc)}
                    />
                  </div>
                </div>
              )}

              {(showBookingFor || showMode) && (
                <div className="cf-row cf-row-3">
                  {/* self (default) or a family member. Changing it re-loads the
                      services available to that person. */}
                  {showBookingFor && (
                    <div className="cf-field">
                      <label htmlFor="bookingfor">
                        Booking for<span className="cf-req">*</span>
                      </label>
                      <Select
                        id="bookingfor"
                        searchable
                        value={memberSel ? memberSel.memberPatientId : null}
                        options={familyMembers}
                        getKey={(m) => m.memberPatientId}
                        /* "(10248) Vineeta Verma (F/52/Self)" — bridge's shape
                           (navigo-module/CreateAppointment.js:2940). Three
                           differences from bridge's literal, all deliberate:
                           the name keeps phr's filter(Boolean) join, because
                           lastName is nullable and bridge's raw interpolation
                           prints "null"; the age is computed HERE rather than by
                           overwriting the row's dateOfBirth with a number, which
                           is what bridge does and which leaves anything else
                           reading that field with an age instead of a date; and
                           an unknown slot reads "-" rather than collapsing, so
                           the three slots always line up. */
                        getLabel={(m) => {
                          const nm = [m.firstName, m.lastName].filter(Boolean).join(" ");
                          const g =
                            m.gender === Gender.FEMALE ? "F" : m.gender === Gender.MALE ? "M" : "-";
                          /* The board writes this triple as "(F/52/Self)" —
                             the slot IS the age, so the unit is redundant
                             there. It is kept only when it changes the meaning:
                             an age under a year reads "(M/7 mo/Self)", which
                             without "mo" would be a seven-year-old. */
                          const age = formatAgeLabel(m.dateOfBirth, m.age).replace(/ yr$/, "");
                          const rel =
                            m.relation === 0
                              ? "Self"
                              : (relations.find((r) => r.code === m.relation) || {}).value || "-";
                          return `(${m.memberPatientId}) ${nm} (${g}/${age || "-"}/${rel})`;
                        }}
                        placeholder={busy === "member" ? "Loading…" : "Select who it's for"}
                        disabled={busy === "member"}
                        onChange={(id) => {
                          const m = familyMembers.find((x) => x.memberPatientId === id);
                          if (m) chooseMember(m);
                        }}
                      />
                    </div>
                  )}

                  {showMode && (
                    <div className="cf-field">
                      <label htmlFor="mode">
                        Select service mode<span className="cf-req">*</span>
                      </label>
                      <Select
                        id="mode"
                        searchable
                        value={mode}
                        options={modes}
                        getKey={(m) => m}
                        /* "Tele consultation (Rs 0)". The price is
                           `discountedPrice` on the selected SERVICE's matching
                           serviceModeDetails row — not the provider's price,
                           which is the amount actually charged and a different
                           number. Bridge re-finds the row by name the same way
                           (navigo-module/CreateAppointment.js:3126).

                           TWO GUARDS BRIDGE DOES NOT HAVE. It interpolates the
                           price raw, so an absent one renders the literal
                           "(Rs undefined)"; here rupees() answers "" for
                           null/undefined/NaN and the parenthetical is dropped,
                           so a free consultation ("Rs 0") and an unknown price
                           cannot read the same. And differential pricing keeps
                           bridge's wording rather than asserting one number for
                           a mode whose price depends on the provider. */
                        getLabel={(m) => {
                          const row = ((selectedService && selectedService.serviceModeDetails) || [])
                            .find((d) => d && d.modeName === m);
                          if (row && row.hasDifferentialPricing) {
                            return `${m} (price varies on service provider)`;
                          }
                          const money = row ? rupees(row.discountedPrice) : "";
                          return money ? `${m} (${money})` : m;
                        }}
                        placeholder="Select a mode"
                        onChange={(m) => chooseMode(m)}
                      />
                      {!modes.length && (
                        <p className="cf-note">
                          This service has no bookable consultation mode.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* the server's "not bookable" answer — normal, not an error */}
              {planBlocked && <p className="cf-blocked">{planBlocked}</p>}
            </section>
          )}

          {/* ─────────── Provider (3041:10114) ─────────── */}
          {showProviderSection && (
            <section className="cf-section">
              <h2 className="cf-section-title">Provider</h2>

              {/* Every provider offering this plan/service/mode, not just the
                  operator's own rows. The operator's own are marked "(you)" and
                  sorted first — a label, not a restriction. Each row's `price`
                  is what gets charged, so changing the pick re-pins the price
                  and clears the slot. */}
              {providerRow && (
                <div className="cf-row cf-row-1">
                  <div className="cf-field">
                    <label className="cf-check">
                      <input
                        type="checkbox"
                        checked={autoSp}
                        disabled={!autoSpAllowed || busy === "provider"}
                        onChange={toggleAutoSp}
                      />
                      Select service provider or check mark to select automatically
                      <span className="cf-req">*</span>
                    </label>
                    {showProviderPick ? (
                    <Select
                      id="provider"
                      searchable
                      value={providerKey(provider)}
                      options={sortedProviders}
                      getKey={providerKey}
                      getLabel={(p) => providerLabel(p, inPerson)}
                      placeholder="Select a service provider"
                      disabled={autoSp}
                      onChange={(key) => {
                        // resetFrom("date") nulls `provider` (and clears date/slot/
                        // vendor), so re-pin the new one AFTER it — otherwise this
                        // handler would immediately erase its own selection.
                        resetFrom("date");
                        /* THE ROSTER TOO, and forgetting it made this field
                           DISAPPEAR the moment the operator changed it.
                           resetFrom("date") empties `providers` on purpose (a new
                           patient must not inherit the previous one's roster), but
                           this field's visibility is computed from that same list —
                           showProviderPick is `providers.length > 1` — so after a
                           change the list was [] and neither the picker nor the
                           read-only row rendered. The booking kept the new provider
                           and its price, with nothing on screen saying who it was.
                           Re-pinned from the closure, exactly like the two lines
                           below; the reference is unchanged, so sortedProviders
                           does not even recompute. */
                        setProviders(providers);
                        /* The ROW the operator picked — its vendor, clinic and
                           price — found by its row key; `providerId` is only
                           the internal id the booking is made against. */
                        const row = providers.find((p) => providerKey(p) === key) || null;
                        setProviderId(row ? row.id : null);
                        setProvider(row);
                      }}
                    />
                    ) : (
                      /* The single auto-picked provider — read-only, so the
                         operator can see who and at what price before booking. */
                      <div className="cf-readonly" id="provider-readonly">
                        {providerReadonly(provider, inPerson, hints.provider)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* in-person only: where the visit is. The provider's one address,
                  shown read-only — nothing to choose, exactly as legacy books
                  it. Only a row that brings several clinics (`addresses[]`)
                  offers a pick, which then supplies providerAddressId to slots
                  + create. No location search (the provider is already chosen). */}
              {/* Full width, like the service and provider rows above it: the
                  address is the LONGEST value on this screen and it was alone
                  in a three-column row, so a third of the form clipped it
                  mid-word (Nittin, 2026-09-24). */}
              {showClinic && (
                <div className="cf-row cf-row-1">
                  <div className="cf-field">
                    <label htmlFor={pickClinic ? "clinic" : "clinic-readonly"}>
                      Clinic address{pickClinic ? <span className="cf-req">*</span> : null}
                    </label>
                    {pickClinic ? (
                      <Select
                        id="clinic"
                        searchable
                        value={clinicId}
                        options={clinics}
                        getKey={(c) => c.providerAddressId}
                        getLabel={(c) =>
                          [c.housing, c.city, c.landmark].filter(Boolean).join(" · ") || "Clinic"
                        }
                        placeholder="Select a clinic"
                        onChange={(id) => {
                          setClinicId(id);
                          setDate("");
                          setSlot(null);
                        }}
                      />
                    ) : addressLine(provider && provider.address) ? (
                      <div className="cf-readonly is-wrap" id="clinic-readonly">
                        {addressLine(provider.address)}
                      </div>
                    ) : (
                      /* Informational, not a gate: legacy books in person
                         without an address too. */
                      <p className="cf-note">No clinic address on file for this provider.</p>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ─────────── Schedule & payment (3041:10141) ─────────── */}
          {showScheduleSection && (
            <section className="cf-section">
              <h2 className="cf-section-title">Schedule &amp; payment</h2>

              <div className="cf-row cf-row-3">
                {showDate && (
                  <div className="cf-field">
                    <label htmlFor="date">
                      Choose date<span className="cf-req">*</span>
                    </label>
                    <DatePicker
                      id="date"
                      value={date}
                      minDays={autoSp ? 0 : provider.minBookingDays || 0}
                      maxDays={autoSp ? AUTO_SP_MAX_DAYS : provider.maxBookingDays || 10}
                      onChange={(d) => {
                        setDate(d);
                        setSlot(null);
                      }}
                    />
                  </div>
                )}

                {showSlot && (
                  <div className="cf-field">
                    <label htmlFor="slot">
                      Choose timeslot<span className="cf-req">*</span>
                    </label>
                    <Select
                      id="slot"
                      searchable
                      value={slot ? slot.key : null}
                      options={slots}
                      getKey={(s) => s.key}
                      getLabel={(s) => formatTimeSlot(s.value, selectedService && selectedService.duration)}
                      placeholder={busy === "slots" ? "Loading…" : "Select a slot"}
                      disabled={busy === "slots"}
                      onChange={(k, s) => setSlot(s)}
                    />
                    {!slots.length && busy !== "slots" && (
                      <p className="cf-note">
                        No slots available on this date. Slots already past — or within the
                        next hour — can&rsquo;t be booked.
                      </p>
                    )}
                  </div>
                )}

                {showTotal && <ReadField label="Total amount" value={priceLabel} />}
              </div>

              {showContactAndReason && (
                <div className="cf-row cf-row-2">
                  {/* a different number to reach the patient on for THIS
                      appointment — tele/video only. */}
                  <div className="cf-field">
                    <label className="cf-check">
                      <input
                        type="checkbox"
                        checked={customContactOn}
                        onChange={(e) => {
                          setCustomContactOn(e.target.checked);
                          if (e.target.checked && !customContact) {
                            setCustomContact(String(patient.phoneno || ""));
                          }
                        }}
                      />
                      Patient contact number
                    </label>
                    {customContactOn ? (
                      <div className="cf-contact">
                        <span className="cf-cc">+{patient.countryCode}</span>
                        <input
                          type="tel"
                          inputMode="numeric"
                          className="cf-input"
                          placeholder="10-digit number"
                          value={customContact}
                          onChange={(e) =>
                            setCustomContact(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))
                          }
                        />
                      </div>
                    ) : (
                      <div className="cf-readonly">
                        {maskPhone(patient.countryCode, patient.phoneno)}
                      </div>
                    )}
                    {customContactOn && !contactValid && (
                      <p className="cf-error">Enter a valid 10-digit number.</p>
                    )}
                  </div>

                  <div className="cf-field">
                    <label htmlFor="reason">
                      Reason for consultation<span className="cf-req">*</span>
                    </label>
                    <Select
                      id="reason"
                      searchable
                      value={reasonId}
                      options={reasons}
                      getKey={(r) => r.id}
                      getLabel={(r) => r.name}
                      placeholder="Select a reason"
                      onChange={(id) => {
                        setReasonId(id);
                        clear("reason");
                      }}
                    />
                  </div>
                </div>
              )}

              {showReasonDetails && (
                <div className="cf-row cf-row-1">
                  <div className="cf-field">
                    <label htmlFor="reason-details">Describe the reason</label>
                    <input
                      id="reason-details"
                      type="text"
                      className="cf-input"
                      placeholder={"Type here — mandatory only if “Other” is selected above"}
                      value={reasonDetails}
                      onChange={(e) => {
                        setReasonDetails(e.target.value);
                        clear("reason");
                      }}
                    />
                    {errors.reason && <p className="cf-error">{errors.reason}</p>}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ─────────── Card footer (3041:10192) ───────────
              The price and the commit sit together, inside the card, so the
              amount being agreed to is next to the button that agrees to it. */}
          <div className="cf-footer">
            {/* Only once there IS an amount. A "Total payable —" on an empty
                form announces a price that has not been worked out yet, which
                reads as a broken figure rather than an absent one. `showTotal`
                is the same condition the summary column uses, so the two cannot
                disagree about whether the price is known. */}
            {showTotal && (
              <div className="cf-payable">
                <span>Total payable</span>
                <strong>{priceLabel}</strong>
              </div>
            )}
            <button type="button" className="zy-btn zy-btn-primary" disabled={!ready} onClick={submit}>
              {price > 0 ? "Continue to payment" : "Create appointment"}
              <ArrowIcon />
            </button>
          </div>
        </div>
      </div>

      {payload && (
        <PaymentLinkDialog
          payload={payload}
          price={price}
          patient={patient}
          onClose={() => setPayload(null)}
          /* The appointment just created, not the list. Bridge lands the
             operator on the booking they made — which is where the payment
             status and the resend path live — and returning to the list makes them
             hunt for it. Falls back to the list when the id is missing (the
             error screens have nothing to open). */
          onDone={(createdId) =>
            history.push(
              createdId ? `/navigo-module/appointment/${createdId}` : "/navigo-module"
            )
          }
        />
      )}

      {backfill && (
        <MissingProfileDialog
          patient={backfill.record}
          needDob={backfill.needDob}
          needEmail={backfill.needEmail}
          onSaved={(updated) => {
            const then = backfill.then;
            // Use the PATCHED record — if we keep the pre-edit snapshot, the
            // primary patient still reads as missing DOB/email and the dialog
            // re-opens (and re-PUTs) the next time "Self" is chosen.
            if (patient && updated && updated.id === patient.id) {
              setPatient((prev) => ({ ...prev, ...updated }));
            }
            setBackfill(null);
            if (then) then();
          }}
          onClose={() => setBackfill(null)}
        />
      )}
    </div>
  );
};

CreateAppointment.propTypes = {
  history: PropTypes.object.isRequired,
};

export default CreateAppointment;
